import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Clipboard, Modal, ScrollView, StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { ActionSheetProvider, type ActionSheetProviderRef } from '@expo/react-native-action-sheet';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { callPartnerOwner, getPartnerOwnerState, newPartnerRequestId, partnerOwnerErrorMessage, type PartnerOwnerScope, type PartnerOwnerState, type PartnerPermission } from '@/services/partnerChecklistOwnerService';
import { hasPartnerChecklistPending } from '@/hooks/usePartnerChecklist';
import { getAndroidActionSheetSafeAreaStyle } from '@/utils/safeArea';
import theme from '@/config/theme';
import { ChecklistFieldAccess } from './ChecklistFieldAccess';

const DEFAULT_TTL_MINUTES = 1440;
const VALIDITY_OPTIONS = [
  { label: '1 hora', minutes: 60 },
  { label: '5 horas', minutes: 300 },
  { label: '12 horas', minutes: 720 },
  { label: '24 horas', minutes: DEFAULT_TTL_MINUTES },
];

export function PartnerChecklistModal({ visible, scope, observedState, prepare, onClose, onChanged }: {
  visible: boolean;
  scope: PartnerOwnerScope;
  observedState?: PartnerOwnerState | null;
  prepare: () => Promise<void>;
  onClose: () => void;
  onChanged: () => Promise<unknown> | void;
}) {
  const [state, setState] = useState<PartnerOwnerState | null>(null);
  const [permissions, setPermissions] = useState<Record<string, PartnerPermission>>({});
  const [allowComplete, setAllowComplete] = useState(false);
  const [ttlMinutes, setTtlMinutes] = useState(DEFAULT_TTL_MINUTES);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const request = useRef<{ signature: string; id: string } | null>(null);
  const issuedLinkId = useRef<string | null>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const actionSheet = useRef<ActionSheetProviderRef>(null);
  const safeAreaInsets = useSafeAreaInsets();
  const validityLabel = VALIDITY_OPTIONS.find(option => option.minutes === ttlMinutes)!.label;
  const identity = JSON.stringify(scope);
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setBusy(true); setError(''); setUrl(''); setCopied(false); setState(null);
    setTtlMinutes(DEFAULT_TTL_MINUTES);
    issuedLinkId.current = null;
    request.current = null;
    void getPartnerOwnerState(scope).then(value => {
      if (!active) return;
      setState(value);
      setPermissions(Object.fromEntries(value.fields.map(field => [field.id, 'hidden'])));
      setAllowComplete(false);
    }).catch(e => { if (active) setError(partnerOwnerErrorMessage(e)); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [visible, identity]);

  useEffect(() => {
    if (!visible || busyRef.current || !observedState ||
        observedState.empresa.id !== scope.companyId || observedState.checklist.id !== scope.checklistId) return;
    let active = true;
    const update = (value: PartnerOwnerState) => {
      if (!active || (state && value.revision < state.revision)) return;
      setState(value);
      if (issuedLinkId.current && value.active_link?.id !== issuedLinkId.current) {
        setUrl(''); setCopied(false); issuedLinkId.current = null;
      }
      if (!value.active) {
        setUrl(''); setCopied(false);
        if (state?.active) request.current = null;
      }
    };
    // Criar/revogar não muda a revisão dos valores; confirme uma transição ambígua antes de trocar o modal.
    if (state?.active && !observedState.active && observedState.revision === state.revision) {
      void getPartnerOwnerState(scope).then(update).catch(() => undefined);
    } else update(observedState);
    return () => { active = false; };
  }, [observedState, visible, identity]);

  const create = async () => {
    setBusy(true); setCreating(true); setError('');
    try {
      await prepare();
      if (await hasPartnerChecklistPending(scope.checklistId)) {
        throw new Error('Existem alterações pendentes. Aguarde a sincronização antes de gerar o link.');
      }
      const signature = JSON.stringify({ permissions, allowComplete, ttlMinutes });
      if (request.current?.signature !== signature) request.current = { signature, id: newPartnerRequestId() };
      const result = await callPartnerOwner<{ url: string; share_link: { id: string } }>(scope, 'create', {
        permissions, allowComplete, ttlMinutes, requestId: request.current.id,
      });
      const confirmed = await getPartnerOwnerState(scope);
      setState(confirmed);
      issuedLinkId.current = confirmed.active_link?.id === result.share_link.id ? result.share_link.id : null;
      setUrl(issuedLinkId.current ? result.url : '');
      setCopied(false);
      request.current = null;
      await onChanged();
    } catch (e) {
      setError(e instanceof Error && e.message.startsWith('Existem alterações') ? e.message : partnerOwnerErrorMessage(e));
    } finally { setBusy(false); setCreating(false); }
  };
  const revoke = async (linkId = state?.active_link?.id) => {
    if (!linkId) return;
    setBusy(true); setError('');
    try {
      await callPartnerOwner(scope, 'revoke', { linkId, requestId: newPartnerRequestId() });
      if (linkId === state?.active_link?.id || linkId === issuedLinkId.current) {
        setUrl(''); setCopied(false); issuedLinkId.current = null;
      }
      setState(await getPartnerOwnerState(scope));
      request.current = null;
      await onChanged();
    } catch (e) { setError(partnerOwnerErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const action = (label: string, run: () => void, disabled = false) => (
    <TouchableOpacity accessibilityRole="button" accessibilityLabel={label} disabled={disabled || busy}
      style={[styles.button, (disabled || busy) && styles.disabled]} onPress={run}>
      <Text style={styles.buttonText}>{label}</Text>
    </TouchableOpacity>
  );
  const selectValidity = () => {
    if (busyRef.current) return;
    actionSheet.current?.showActionSheetWithOptions({
      options: ['Cancelar', ...VALIDITY_OPTIONS.map(option => option.label)],
      cancelButtonIndex: 0,
      title: 'Validade do link e do comprovante',
      useModal: false,
      titleTextStyle: { color: theme.colors.text.primary, fontSize: theme.fontSizes.md },
      containerStyle: {
        backgroundColor: theme.colors.background.primary,
        ...getAndroidActionSheetSafeAreaStyle({ bottomInset: safeAreaInsets.bottom }),
      },
      textStyle: { color: theme.colors.primary.default, fontSize: theme.fontSizes.md },
    }, buttonIndex => {
      if (busyRef.current || buttonIndex === undefined || buttonIndex === 0) return;
      const selected = VALIDITY_OPTIONS[buttonIndex - 1];
      if (selected) setTtlMinutes(selected.minutes);
    });
  };
  const groups = new Map<string, NonNullable<typeof state>['fields']>();
  for (const field of state?.fields ?? []) {
    const group = field.grupo?.trim() || 'Checklist';
    groups.set(group, [...(groups.get(group) ?? []), field]);
  }
  const hasSharedFields = Object.values(permissions).some(permission => permission !== 'hidden');
  return <Modal visible={visible} transparent animationType="fade" onRequestClose={() => { if (!busy) onClose(); }}>
    {/* O seletor usa a mesma janela nativa do modal para não ficar atrás dela no Android. */}
    <ActionSheetProvider ref={actionSheet}>
    <View style={styles.backdrop}><View style={styles.card}>
      <Text style={styles.title}>Parceiro preencher por link</Text>
      <Text style={styles.body}>Sem login. O parceiro preenche este mesmo checklist, incluindo fotos, assinaturas e marcações que você liberar.</Text>
      {busy && <View accessibilityLiveRegion="polite">
        <ActivityIndicator accessibilityLabel={creating ? 'Gerando link' : 'Confirmando operação'} color={theme.colors.primary.default} />
        {creating && <Text style={styles.body}>Gerando link. Aguarde a confirmação.</Text>}
      </View>}
      {!!error && <Text accessibilityRole="alert" style={styles.error}>{error}</Text>}
      <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
      {state?.active ? <View>
        <Text style={styles.body}>O link está ativo. A edição no app fica bloqueada até o parceiro enviar o preenchimento ou você encerrar o acesso.</Text>
        <Text style={styles.body}>Validade: {new Date(state.active_link!.expires_at).toLocaleString('pt-BR')}</Text>
        {!!url && <Text selectable style={styles.link}>{url}</Text>}
        {!!url && action(copied ? 'Link copiado' : 'Copiar link', () => { Clipboard.setString(url); setCopied(true); })}
        {!url && <Text style={styles.body}>Para obter outro link, encerre este acesso e gere um novo.</Text>}
        {action('Encerrar acesso e retomar', () => { void revoke(); })}
      </View> : state?.checklist.status === 'in_progress' ? <View>
        <Text style={styles.body}>Escolha o que poderá ser visto e preenchido. O prazo escolhido vale para o link e o comprovante.</Text>
        <Text style={styles.body}>{state.touched
          ? 'Este checklist usa o acesso de parceiro. Para compartilhar novamente, gere outro link aqui.'
          : 'O primeiro link substitui Cliente preencher apenas neste checklist. Depois, continue compartilhando por este acesso de parceiro.'}</Text>
        <View style={styles.row}>
          {action('Ocultar todos', () => setPermissions(Object.fromEntries(state.fields.map(f => [f.id, 'hidden']))))}
          {action('Permitir ver todos', () => setPermissions(Object.fromEntries(state.fields.map(f => [f.id, 'read']))))}
        </View>
        {[...groups].map(([group, fields]) => <View key={group}>
          <Text accessibilityRole="header" style={styles.groupTitle}>{group}</Text>
          {fields.map(field => <ChecklistFieldAccess key={field.id}
            name={field.nome} required={field.obrigatorio}
            value={permissions[field.id] ?? 'hidden'} disabled={busy}
            onChange={permission => setPermissions(previous => ({ ...previous, [field.id]: permission }))} />)}
        </View>)}
        <View style={styles.row}>
          <Switch value={allowComplete} onValueChange={setAllowComplete} disabled={busy} accessibilityLabel="Permitir concluir o checklist" />
          <Text style={styles.body}>Permitir concluir o checklist</Text>
        </View>
        <Text style={styles.body}>{allowComplete
          ? 'O parceiro também poderá concluir, depois que todos os campos obrigatórios estiverem salvos.'
          : 'Ao enviar, o parceiro encerra seu preenchimento. O checklist continua em andamento para você conferir e concluir.'}</Text>
        {!hasSharedFields && <Text style={styles.body}>Selecione pelo menos um campo como Ver ou Preencher para gerar o link.</Text>}
        <View style={styles.validity}>
          <Text style={styles.validityLabel}>Validade do link e do comprovante</Text>
          <TouchableOpacity accessibilityRole="button" accessibilityLabel="Validade do link e do comprovante"
            accessibilityValue={{ text: validityLabel }} accessibilityState={{ disabled: busy }} disabled={busy}
            style={[styles.selectButton, busy && styles.disabled]} onPress={selectValidity}>
            <Text style={styles.selectValue}>{validityLabel}</Text>
            <Ionicons name="chevron-down" size={18} color={theme.colors.text.secondary} />
          </TouchableOpacity>
        </View>
        {action(creating ? 'Gerando link…' : 'Gerar link', () => { void create(); }, !state.enabled || !hasSharedFields)}
      </View> : state && <Text style={styles.body}>Checklist concluído. Você pode retirar o acesso aos comprovantes ainda válidos abaixo.</Text>}
      {(state?.receipt_links ?? []).map(link => <View key={link.id} style={styles.receipt}>
        <Text style={styles.body}>Comprovante dos campos compartilhados até {new Date(link.expires_at).toLocaleString('pt-BR')}.</Text>
        {action('Retirar acesso ao comprovante', () => { void revoke(link.id); })}
      </View>)}
      </ScrollView>
      {action('Fechar', onClose)}
    </View></View>
    </ActionSheetProvider>
  </Modal>;
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', padding: 16, alignItems: 'center', justifyContent: 'center' },
  card: { backgroundColor: '#fff', borderRadius: 18, padding: 20, width: '100%', maxWidth: 640, maxHeight: '92%', gap: 10 },
  title: { fontSize: 20, fontWeight: '700', color: '#182c40' },
  body: { fontSize: 14, color: '#475569', lineHeight: 21, flexShrink: 0 },
  error: { color: '#b42318', fontSize: 14, lineHeight: 21 },
  scroll: { flexShrink: 1, minHeight: 0 },
  groupTitle: { fontSize: 14, fontWeight: '700', color: '#182c40', marginTop: 16 },
  receipt: { borderTopWidth: 1, borderColor: '#e2e8f0', marginTop: 12, paddingTop: 12 },
  row: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginVertical: 6 },
  validity: { marginVertical: 12 },
  validityLabel: { fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary, marginBottom: 4 },
  selectButton: { backgroundColor: theme.colors.background.primary, borderRadius: theme.borderRadius.sm,
    borderWidth: 1, borderColor: theme.colors.border.light, paddingHorizontal: theme.spacing.sm + 4,
    minHeight: 44, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  selectValue: { fontSize: theme.fontSizes.sm, color: theme.colors.text.primary },
  button: { padding: 12, borderRadius: 9, backgroundColor: '#eef6fa', marginVertical: 4 },
  buttonText: { color: '#155d82', fontWeight: '600', textAlign: 'center' },
  disabled: { opacity: 0.5 },
  link: { color: '#155d82', fontSize: 12, marginVertical: 8 },
});
